import { Box, CircularProgress, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouteLink, useHistory } from 'react-router-dom';

import { DeviceHardware, DeviceV3 } from '../../../../../../backendsdk';
import { TrackedButton as Button, TrackedLink as Link } from '../../../../../../components/TrackedComponents';
import { useAlert } from '../../../../../../hooks/alert';
import useApi from '../../../../../../hooks/api';
import useProfile from '../../../../../../hooks/profile';
import { VideoStreamType } from '../../../../../../utils/ImageDimensions';
import { HORIZONTAL_APERTURE, VERTICAL_APERTURE, WorkerParams } from '../../SetupTool';
import ImageNA from '../../assets/image_not_available.png';
import { AnalysisResult, fallbackValue } from '../../utils/analysis';
import getWorker from '../../workers/worker';
import { convertParam } from '../InstallationParams';
import { CameraStreamView } from './CameraStreamView';
import DeviceSelector from './DeviceSelector';
import { ObjectDistanceInput } from './ObjectDistanceInput';
import { useStreamNavigation } from './useStreamNavigation';

interface VerifyCameraProps {
    malfunctionId?: number;
    installerName: string;
    customerName: string;
    onBack: () => void;
    imei?: string;
    hardware: DeviceHardware;
    dashboardLink: React.ReactNode;
}

const VerifyCamera: React.FC<VerifyCameraProps> = (props) => {
    const [imei, setImei] = useState<string>(props.imei || '');
    const [selectedDevice, setSelectedDevice] = useState<DeviceV3 | null>(null);
    const [showObjectInput, setShowObjectInput] = useState<boolean>(false);
    const [snapshot, setSnapshot] = useState<string>('');
    const [isLoadingAction, setIsLoadingAction] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(!!props.imei);
    const [isError, setIsError] = useState<boolean>(false);
    const [workerParams, setWorkerParams] = useState<WorkerParams | undefined>();
    const [horizontalPixel, setHorizontalPixel] = useState<number>();
    const [minDistance, setMinDistance] = useState<number>();
    const [groundPixel, setGroundPixel] = useState<number>();
    const [objectOverlay, setObjectOverlay] = useState<boolean>(false);
    const [enteredDistance, setEnteredDistance] = useState<string>('');
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [imageCounter, setImageCounter] = useState<number>(0);
    const [keyCounter, setKeyCounter] = useState<number>(0);

    const {
        currentImageIndex,
        availableStreams,
        setCurrentImageIndex,
        detectStreamsFromImage,
        resetNavigation,
        isLastImage,
        currentStream,
    } = useStreamNavigation();
    const [viewedStreams, setViewedStreams] = useState<Set<VideoStreamType>>(new Set());
    const { t } = useTranslation();
    const { api, installApi } = useApi();
    const { profile } = useProfile();
    const history = useHistory();
    const [alertElement, setAlert] = useAlert();
    const isUS = profile.customer.settings.country === 'us';
    const isMetric = !isUS;
    const units = isMetric ? 'meters' : 'inches';
    const localeMinDistance = !!minDistance
        ? isMetric
            ? minDistance + 0.5
            : convertParam(minDistance + 0.5, 'METRIC', 'IMPERIAL')
        : undefined;
    const isDistanceValid =
        minDistance !== undefined &&
        !!enteredDistance &&
        !isNaN(parseFloat(enteredDistance)) &&
        parseFloat(enteredDistance) >= minDistance;
    const isActionDisabled = !!imei ? isError || !snapshot || isLoadingAction : !selectedDevice?.current_trip;

    useEffect(() => {
        if (workerParams) {
            const worker = getWorker();
            worker.onmessage = (event) => {
                const res = event.data as AnalysisResult;
                setHorizontalPixel(res.segmentPixel);
                setMinDistance(res.minDistance);
                setGroundPixel(res.groundPixel);
            };
            worker.onerror = () => {
                setHorizontalPixel(fallbackValue);
            };
            worker.postMessage(workerParams);
            return () => {
                worker.terminate();
            };
        }
    }, [workerParams]);

    const getInstallationParams = () => {
        installApi
            .installParamsDeviceIdGet({ deviceId: imei })
            .then((res) => {
                const installationParams = res.data.installation_params;
                const workerParams = {
                    instHeight: !!installationParams ? parseFloat(installationParams.instHeight) : undefined,
                    instDepth: !!installationParams ? parseFloat(installationParams.instDepth) : undefined,
                    instHoodHeight: !!installationParams ? parseFloat(installationParams.instHoodHeight) : undefined,
                    'vehicle-width': !!installationParams ? parseFloat(installationParams['vehicle-width']) : undefined,
                    horizontalAperture: HORIZONTAL_APERTURE[props.hardware],
                    verticalAperture: VERTICAL_APERTURE[props.hardware],
                };
                if (Object.values(workerParams).some((val) => val === undefined)) {
                    setHorizontalPixel(fallbackValue);
                } else {
                    setWorkerParams(workerParams as WorkerParams);
                }
            })
            .catch(() => setHorizontalPixel(fallbackValue));
    };

    const getSnapshot = (updateStreams: boolean, controller?: AbortController) => {
        const now = new Date().getTime() / 1000;
        const since = now - 60;
        api.apiV0DeviceDeviceIdSnapshotGet(
            { deviceId: imei, timestamp: now, since: since },
            { responseType: 'blob', signal: controller?.signal },
        )
            .then((res) => {
                if (controller?.signal.aborted) {
                    return;
                }
                setImageCounter(0);
                setKeyCounter((prev) => prev + 1);
                const reader = new window.FileReader();
                reader.readAsDataURL(res.data);
                reader.onload = () => {
                    const image = reader.result;
                    if (typeof image === 'string') {
                        setSnapshot(image);
                        setIsError(false);
                        if (updateStreams) {
                            detectStreamsFromImage(image);
                            setObjectOverlay(false);
                            setShowObjectInput(false);
                            setEnteredDistance('');
                            setErrorMessage('');
                        }
                    }
                };
                reader.onerror = () => {
                    if (updateStreams) {
                        setSnapshot(ImageNA);
                    }
                    setIsError(true);
                };
            })
            .catch(() => {
                if (controller?.signal.aborted) {
                    return;
                }
                setImageCounter(0);
                if (updateStreams) {
                    setSnapshot(ImageNA);
                }
                setIsError(true);
            })
            .finally(() => setIsLoading(false));
    };

    useEffect(() => {
        const controller = new AbortController();
        if (!!imei) {
            getInstallationParams();
            getSnapshot(true, controller);
            setViewedStreams(new Set());
            return () => {
                controller.abort();
            };
        }
    }, [imei]);

    useEffect(() => {
        let timerId: NodeJS.Timeout;
        if (!!imei) {
            if (imageCounter >= 20) {
                const controller = new AbortController();
                // wait for progress bar animation to end
                timerId = setTimeout(() => {
                    // update streams if snapshot is not loaded yet
                    getSnapshot(!snapshot, controller);
                }, 400);
                return () => {
                    clearTimeout(timerId);
                    controller.abort();
                };
            } else {
                timerId = setTimeout(() => {
                    setImageCounter((prev) => prev + 1);
                }, 1000);
                return () => {
                    clearTimeout(timerId);
                };
            }
        }
    }, [imei, imageCounter]);

    const convertToBase64 = (url: string): Promise<string> => {
        return new Promise((resolve, reject) => {
            fetch(url)
                .then((response) => response.blob())
                .then((blob) => {
                    const reader = new FileReader();
                    reader.readAsDataURL(blob);
                    reader.onloadend = () => {
                        resolve(reader.result as string);
                    };
                    reader.onerror = () => {
                        reject();
                    };
                })
                .catch(() => {
                    reject();
                });
        });
    };

    const handleDone = async () => {
        setIsLoadingAction(true);
        try {
            const base64Image = await convertToBase64(snapshot);
            await installApi.installVerifyPost({
                verifyCamerasRequest: {
                    device_id: imei,
                    installer: props.installerName,
                    snapshot: base64Image,
                    malfunction_id: props.malfunctionId,
                },
            });
            history.push(props.malfunctionId !== undefined ? `/tasks/${props.malfunctionId}` : '/tasks');
        } catch {
            setAlert({ message: t('setup_tool.verify_camera.error'), type: 'error', duration: 6000 });
        } finally {
            setIsLoadingAction(false);
        }
    };

    let content;
    if (!imei) {
        content = <DeviceSelector onDeviceChange={setSelectedDevice} />;
    } else if (showObjectInput) {
        content = (
            <ObjectDistanceInput
                enteredDistance={enteredDistance}
                onDistanceChange={setEnteredDistance}
                errorMessage={errorMessage}
                localeMinDistance={localeMinDistance}
                units={units}
            />
        );
    } else if (currentStream) {
        content = (
            <CameraStreamView
                key={currentImageIndex}
                stream={currentStream}
                availableStreams={availableStreams}
                viewedStreams={viewedStreams}
                snapshot={snapshot}
                isError={isError}
                imageCounter={imageCounter}
                keyCounter={keyCounter}
                hardware={props.hardware}
                horizontalPixel={horizontalPixel}
                groundPixel={groundPixel}
                objectOverlay={objectOverlay}
                localeMinDistance={localeMinDistance}
                isActionDisabled={isActionDisabled}
                onNoHorizonClick={() => {
                    setShowObjectInput(true);
                }}
                onStreamSelected={(stream) => {
                    setViewedStreams((prev) => new Set([...prev, stream]));
                }}
            />
        );
    }

    return (
        <Box
            sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
            }}
        >
            {alertElement}
            <Box
                sx={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    minHeight: 0,
                }}
            >
                <Typography variant="h6" textAlign="center">
                    {t('setup_tool.verify_camera.header')}
                </Typography>
                <Typography fontSize={14} textAlign="center" sx={{ fontWeight: 500, mb: 1 }}>
                    {t('setup_tool.verify_camera.instructions')}
                </Typography>
                {!!imei && (isLoading || !snapshot) ? (
                    <CircularProgress sx={{ mt: 1 }} data-testid="loading-progress" />
                ) : (
                    <Box
                        sx={{
                            width: !imei ? '100%' : undefined,
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            overflowY: 'auto',
                            my: 1,
                        }}
                    >
                        {content}
                    </Box>
                )}
            </Box>
            <Box
                sx={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    flexDirection: 'column',
                    minHeight: 0,
                    flexShrink: 0,
                    py: 2,
                }}
            >
                {isLastImage ? (
                    <Link
                        id="adjust-params-link"
                        data-testid="adjust-params-link"
                        component={RouteLink}
                        to={
                            props.malfunctionId !== undefined ? `/tasks/${props.malfunctionId}/adjust` : '/tasks/adjust'
                        }
                        sx={{ fontSize: 14, mb: 1 }}
                    >
                        {t('setup_tool.select_mode.adjust_header')}
                    </Link>
                ) : null}
                <Box sx={{ width: '100%', display: 'flex' }}>
                    <Box sx={{ flex: 1, mr: 1 }}>
                        <Button
                            id="btn-back"
                            fullWidth
                            variant="contained"
                            color="primary"
                            onClick={() => {
                                if (showObjectInput) {
                                    setShowObjectInput(false);
                                    setErrorMessage('');
                                } else if (currentImageIndex > 0) {
                                    setCurrentImageIndex((prev) => prev - 1);
                                    setErrorMessage('');
                                } else {
                                    if (props.imei !== undefined) {
                                        props.onBack();
                                    } else {
                                        if (imei) {
                                            setImei('');
                                            resetNavigation();
                                        } else {
                                            props.onBack();
                                        }
                                    }
                                }
                            }}
                        >
                            {t('setup_tool.back')}
                        </Button>
                    </Box>
                    <Box sx={{ flex: 1 }}>
                        <Button
                            id="btn-next"
                            data-testid="btn-next"
                            fullWidth
                            variant="contained"
                            color="secondary"
                            onClick={() => {
                                if (!imei && !!selectedDevice) {
                                    setImei(selectedDevice.device.device_id);
                                    setIsLoading(true);
                                } else if (showObjectInput) {
                                    if (isDistanceValid) {
                                        setWorkerParams((prev) => {
                                            if (!prev) {
                                                return prev;
                                            }
                                            return {
                                                ...prev,
                                                distance: isMetric
                                                    ? parseFloat(enteredDistance)
                                                    : convertParam(parseFloat(enteredDistance), 'METRIC', 'IMPERIAL'),
                                            };
                                        });
                                        setObjectOverlay(true);
                                        setShowObjectInput(false);
                                        // Show front facing camera again with object overlay
                                        const frontFacingIndex = availableStreams.findIndex(
                                            (s) => s === VideoStreamType.FrontFacingCamera,
                                        );
                                        if (frontFacingIndex >= 0) {
                                            setCurrentImageIndex(frontFacingIndex);
                                        }
                                    } else {
                                        setErrorMessage(t('setup_tool.device_feedback.object_distance_error'));
                                    }
                                } else if (!isLastImage) {
                                    setCurrentImageIndex((prev) => prev + 1);
                                } else {
                                    handleDone();
                                }
                            }}
                            disabled={isActionDisabled}
                        >
                            {showObjectInput || !imei || !isLastImage || isLoading
                                ? t('setup_tool.next')
                                : t('setup_tool.completed.done')}
                        </Button>
                    </Box>
                </Box>
                {isError ? props.dashboardLink : null}
            </Box>
        </Box>
    );
};

export default VerifyCamera;
